/**
 * Audit Log Page
 *
 * Route: /audit-log | Nav: Reports > Audit Log | Roles: TenantAdmin/Admin
 *
 * Full-page activity log with filtering, search, and CSV export.
 *
 * Migrated onto the List & Grid Standard — see `src/pages/ApiKeysPage.tsx` (reference
 * migration) and `.specify/journals/list-grid-adoption-journal.md` (migration recipe).
 * Read-only list (audit trail) — no Actions column, no detail route.
 */
import { useEffect, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Body1,
  Body2,
  Badge,
  tokens,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Input,
  Dropdown,
  Option,
  Field,
  makeStyles,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
  DocumentBulletListRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../services/api';
import type { ActivityLogEntry, ActivityType } from '../types';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ACTIVITY_TYPES: ActivityType[] = [
  'scan',
  'integration',
  'user',
  'alert',
  'apikey',
  'finding',
  'compliance',
  'settings',
  'authentication',
];

const getTypeBadgeColor = (
  type: ActivityType
): 'informative' | 'success' | 'warning' | 'danger' | 'important' | 'subtle' => {
  switch (type) {
    case 'scan':
      return 'informative';
    case 'integration':
      return 'success';
    case 'user':
      return 'important';
    case 'alert':
      return 'warning';
    case 'apikey':
      return 'subtle';
    case 'finding':
      return 'danger';
    case 'compliance':
      return 'success';
    case 'settings':
      return 'subtle';
    case 'authentication':
      return 'important';
    default:
      return 'informative';
  }
};

const formatTimestamp = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const exportToCsv = (entries: ActivityLogEntry[]) => {
  const headers = ['Timestamp', 'Type', 'Message', 'User', 'Success'];
  const rows = entries.map((e) => [
    e.timestamp,
    e.type,
    `"${(e.message || '').replace(/"/g, '""')}"`,
    e.userDisplayName || '',
    e.success ? 'Yes' : 'No',
  ]);

  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/** Date-range predicate shared between the grid's client-mode filter and the CSV export set. */
const dateInRange = (entry: ActivityLogEntry, dateFrom: string, dateTo: string): boolean => {
  if (dateFrom) {
    if (new Date(entry.timestamp) < new Date(dateFrom)) return false;
  }
  if (dateTo) {
    const toDate = new Date(dateTo);
    toDate.setHours(23, 59, 59, 999);
    if (new Date(entry.timestamp) > toDate) return false;
  }
  return true;
};

/** `type`/`dateFrom`/`dateTo` encoded into the shared `filters` URL param (§8/§19) — a
 *  bespoke serialization (always `eq`) rather than the full `useColumnFilters` machinery,
 *  since this page only has 3 simple filter controls. */
interface AuditFilterParts {
  type: string;
  dateFrom: string;
  dateTo: string;
}

const parseAuditFilters = (raw: string): AuditFilterParts => {
  const result: AuditFilterParts = { type: '', dateFrom: '', dateTo: '' };
  if (!raw) return result;
  for (const part of raw.split(';')) {
    const [field, , value] = part.split(':');
    if (field === 'type' && value) result.type = value;
    if (field === 'dateFrom' && value) result.dateFrom = value;
    if (field === 'dateTo' && value) result.dateTo = value;
  }
  return result;
};

const serializeAuditFilters = (parts: AuditFilterParts): string => {
  const segments: string[] = [];
  if (parts.type) segments.push(`type:eq:${parts.type}`);
  if (parts.dateFrom) segments.push(`dateFrom:eq:${parts.dateFrom}`);
  if (parts.dateTo) segments.push(`dateTo:eq:${parts.dateTo}`);
  return segments.join(';');
};

/** Wraps the (type-filtered, unpaged) `adminApi.getActivityLog` response as a `PagedResult`
 *  — `useAdaptiveGridData` then applies search/date/sort/paging in memory (client mode). */
async function fetchAuditLogPage(
  typeFilter: ActivityType | '',
  params: ListQueryParams
): Promise<PagedResult<ActivityLogEntry>> {
  const items = await adminApi.getActivityLog(typeFilter || undefined, 500);
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

const useStyles = makeStyles({
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalL,
  },
  userText: {
    color: tokens.colorNeutralForeground3,
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  failureIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
});

const crumbs: Crumb[] = [{ label: 'Audit Log' }];

// ---------------------------------------------------------------------------
// AuditLogPage
// ---------------------------------------------------------------------------

export const AuditLogPage: FC = () => {
  const styles = useStyles();

  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({
    defaultSort: 'timestamp:desc',
  });

  const filterParts = parseAuditFilters(state.filters);
  // reason: parseAuditFilters returns a plain string from the URL; ACTIVITY_TYPES is the
  // full enumeration of valid values and the Dropdown only ever writes one of them back.
  const typeFilter = filterParts.type as ActivityType | '';
  const { dateFrom, dateTo } = filterParts;

  const updateFilters = (updates: Partial<AuditFilterParts>) => {
    setFilters(serializeAuditFilters({ ...filterParts, ...updates }));
  };

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const grid = useAdaptiveGridData<ActivityLogEntry>({
    queryKeyBase: ['audit-log', typeFilter],
    fetchPage: (params) => fetchAuditLogPage(typeFilter, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['message', 'userDisplayName'],
    clientPredicate: (item) => dateInRange(item, dateFrom, dateTo),
    sortState,
  });

  const {
    items: entries,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after a filter change.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  // Independent (type-filtered, unpaged) query feeding CSV export — the export must cover
  // every filtered row, not just the current page slice `useAdaptiveGridData` returns.
  const exportQuery = useQuery({
    queryKey: ['audit-log-export', typeFilter],
    queryFn: () => adminApi.getActivityLog(typeFilter || undefined, 500),
  });

  const exportEntries = useMemo(() => {
    const items = exportQuery.data ?? [];
    return items.filter((e) => {
      if (state.search) {
        const q = state.search.toLowerCase();
        const matchesMessage = (e.message || '').toLowerCase().includes(q);
        const matchesUser = (e.userDisplayName || '').toLowerCase().includes(q);
        if (!matchesMessage && !matchesUser) return false;
      }
      return dateInRange(e, dateFrom, dateTo);
    });
  }, [exportQuery.data, state.search, dateFrom, dateTo]);

  const columnCount = 5;

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={8} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load the audit log." onRetry={refetch} />;
  } else if (entries.length === 0) {
    listBody = (
      <EmptyState
        icon={<DocumentBulletListRegular />}
        title="No Audit Log Entries"
        description="No activity matches the current filters."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Audit log">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'timestamp' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('timestamp')}
                onClick={() => toggleSort('timestamp')}
              >
                Timestamp
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'type' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('type')}
                onClick={() => toggleSort('type')}
              >
                Type
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'message' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('message')}
                onClick={() => toggleSort('message')}
                style={{ width: '100%' }}
              >
                Message
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'userDisplayName' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('userDisplayName')}
                onClick={() => toggleSort('userDisplayName')}
              >
                User
              </TableHeaderCell>
              <TableHeaderCell>Result</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Body2>{formatTimestamp(item.timestamp)}</Body2>
                </TableCell>
                <TableCell>
                  <Badge appearance="filled" color={getTypeBadgeColor(item.type)}>
                    {item.type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body1>{item.message}</Body1>
                </TableCell>
                <TableCell>
                  <Body2 className={styles.userText}>{item.userDisplayName || '-'}</Body2>
                </TableCell>
                <TableCell>
                  {item.success ? (
                    <CheckmarkCircle20Regular className={styles.successIcon} />
                  ) : (
                    <DismissCircle20Regular className={styles.failureIcon} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <ListPageLayout
      header={
        <MasterDetailHeader
          crumbs={crumbs}
          busy={isFetching && !isLoading}
          actions={
            <Button
              appearance="primary"
              icon={<ArrowDownloadRegular />}
              onClick={() => exportToCsv(exportEntries)}
              disabled={exportEntries.length === 0}
            >
              Export CSV
            </Button>
          }
        />
      }
      toolbar={
        <>
          <div className={styles.filterRow}>
            <Field label="Type">
              <Dropdown
                placeholder="All Types"
                value={typeFilter}
                onOptionSelect={(_, data) => updateFilters({ type: data.optionValue || '' })}
                style={{ minWidth: '10rem' }}
              >
                <Option value="">All Types</Option>
                {ACTIVITY_TYPES.map((t) => (
                  <Option key={t} value={t}>
                    {t}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            <Field label="From">
              <Input
                type="date"
                value={dateFrom}
                onChange={(_, data) => updateFilters({ dateFrom: data.value })}
              />
            </Field>

            <Field label="To">
              <Input type="date" value={dateTo} onChange={(_, data) => updateFilters({ dateTo: data.value })} />
            </Field>
          </div>

          <ListToolbar
            search={state.search}
            onSearchChange={setSearch}
            searchPlaceholder="Search messages or users…"
            onRefresh={refetch}
            isRefreshing={isFetching}
            testIdPrefix="audit-log"
          />
        </>
      }
      footer={
        <Pager
          page={state.page}
          totalPages={totalPages}
          hasPreviousPage={hasPreviousPage}
          hasNextPage={hasNextPage}
          totalCount={totalCount}
          onPageChange={setPage}
          testId="audit-log-pager"
        />
      }
    >
      {listBody}
    </ListPageLayout>
  );
};
